import { useQuery } from '@tanstack/react-query';

import {
  isOnlinePaymentMethod,
  type OnlinePaymentMethod,
} from '~entities/payment/api/get-allowed-payment-methods';
import type { SavedPaymentMethod } from '~entities/payment/api/get-parent-saved-payment-methods';
import { PaymentMethod } from '~entities/wallet/model/types';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

export enum EnrollmentPaymentProvider {
  STRIPE = 'stripe',
  SQUARE = 'square',
}

export enum EnrollmentPaymentMethodsSource {
  INTERNAL = 'internal',
  STRIPE = 'stripe',
  SQUARE = 'square',
}

export interface EnrollmentPaymentMethodsResponse {
  paymentProvider: EnrollmentPaymentProvider;
  allowedTypes: string[];
  savedMethods: SavedPaymentMethod[];
  defaultPaymentMethodId: string | null;
  hasPaymentMethod: boolean;
  cardSurchargeEligible: boolean;
  bypassStripePaymentMethodConfig?: boolean;
  source?: EnrollmentPaymentMethodsSource;
}

/**
 * V2 enrollment / studio allow-list.
 * Omit `parentId` for studio settings (preference / provider types only).
 */
export const getEnrollmentPaymentMethods = async (
  parentId?: string,
): Promise<EnrollmentPaymentMethodsResponse> => {
  const trimmed = parentId?.trim();
  const response = await baseAPI.get<EnrollmentPaymentMethodsResponse>(
    '/v2/payment/enrollment-methods',
    trimmed ? { params: { parentId: trimmed } } : undefined,
  );
  return response.data;
};

export const useGetEnrollmentPaymentMethods = (params: {
  parentId?: string;
  enabled?: boolean;
  /** When true, fetch studio allow-list without a parent (settings). */
  studioOnly?: boolean;
}) => {
  const parentId = params.parentId?.trim() ?? '';
  const studioOnly = params.studioOnly === true;
  const enabled =
    params.enabled !== false && (studioOnly || !!parentId);

  return useQuery({
    queryKey: [
      KEYS.ENROLLMENT_PAYMENT_METHODS,
      studioOnly ? 'studio' : parentId,
    ],
    queryFn: () =>
      getEnrollmentPaymentMethods(studioOnly ? undefined : parentId),
    enabled,
  });
};

export const toOnlinePaymentMethod = (
  type: string | undefined,
): OnlinePaymentMethod | undefined => {
  if (!isOnlinePaymentMethod(type)) {
    return undefined;
  }
  // Link is a card wallet for enrollment charge selection.
  return type === PaymentMethod.LINK ? PaymentMethod.CARD : type;
};

/**
 * Prefer the catalog default when it is still in `savedMethods`;
 * otherwise the first saved method. Handles a stale defaultPaymentMethodId.
 */
export const pickEnrollmentCatalogDefaultMethod = (
  catalog: EnrollmentPaymentMethodsResponse | undefined,
): SavedPaymentMethod | undefined => {
  if (!catalog || catalog.savedMethods.length === 0) {
    return undefined;
  }
  const { savedMethods, defaultPaymentMethodId } = catalog;
  return (
    (defaultPaymentMethodId
      ? savedMethods.find((method) => method.id === defaultPaymentMethodId)
      : undefined) ?? savedMethods[0]
  );
};

const isCardLikeSavedMethodType = (type: string): boolean =>
  type === PaymentMethod.CARD || type === PaymentMethod.LINK;

const isBankSavedMethodType = (type: string): boolean =>
  type === PaymentMethod.US_BANK_ACCOUNT ||
  type === PaymentMethod.AU_BECS_DEBIT;

export const formatEnrollmentSavedMethodLabel = (
  method: SavedPaymentMethod,
): string => {
  if (isCardLikeSavedMethodType(method.type)) {
    const brandRaw = method.brand?.trim();
    const brand = brandRaw
      ? brandRaw.charAt(0).toUpperCase() + brandRaw.slice(1)
      : method.type === PaymentMethod.LINK
        ? 'Link'
        : 'Card';
    return method.last4 ? `${brand} ****${method.last4}` : brand;
  }

  if (isBankSavedMethodType(method.type)) {
    const bank = method.bankName?.trim() || 'Bank account';
    return method.last4 ? `${bank} ****${method.last4}` : bank;
  }

  return method.type;
};

export const isPaymentTypeAllowed = (
  allowedTypes: string[] | undefined,
  type: OnlinePaymentMethod,
): boolean => {
  if (!allowedTypes?.length) return false;
  if (type === PaymentMethod.CARD) {
    return (
      allowedTypes.includes(PaymentMethod.CARD) ||
      allowedTypes.includes(PaymentMethod.LINK)
    );
  }
  return allowedTypes.includes(type);
};
